// hooks/useContextManagement.ts
import { useCallback } from 'react';

import { GeneratedContentTypeEnum } from '../components/savi-content-panels/types';
import { useChatOverlayContext } from '../context/chatOverlayContext';
import { useIspmContext } from '../context/ispmContext';
import { useIspmErrorContext } from '../context/ispmErrorContext';
import {
  type AskApiRequest,
  type AskApiResponse,
  type RequestWithFreeText,
  type SampleDataApiRequest,
  type SampleDataAPIResponse,
} from '../data-layer/types';

import useMessageHandlers from './use-messagehandlers';

/**
 * A custom hook that manages the application context before and after making API calls.
 * It handles updates to the chat overlay and ISPM states, manages session-related messages,
 * and prepares the UI for API requests and responses.
 *
 * @returns {Object} - An object containing functions to update context before and after API calls.
 */
const useContextManagement = () => {
  const { updateChatOverlayState } = useChatOverlayContext();
  const { updateIspmState } = useIspmContext();
  const { updateApiResponseInSession, updateUserMessageInSession } =
    useMessageHandlers();
  const { updateIspmErrorState } = useIspmErrorContext();

  /**
   * Updates the application context before making an API call.
   * This function:
   * - Resets certain UI inputs.
   * - Disables the input field and displays a loading screen.
   * - Marks the API call as "in progress."
   * - Saves the user's message in the session.
   *
   * @param {AskApiRequest | SampleDataApiRequest} request - The API request payload.
   */
  const updateContextBeforeMakingApiCall = useCallback(
    (request: AskApiRequest | SampleDataApiRequest) => {
      // Reset the input value in ISPM state
      updateIspmState('ispmInputValue', '');
      updateChatOverlayState('saviInputText', '');

      // Disable user input and show loading screen
      updateChatOverlayState('inputDisabled', true);
      // Enable left hand side content panel
      updateChatOverlayState('showGeneratedContent', true);
      // Display loading screen there
      updateChatOverlayState(
        'contentPanelKind',
        GeneratedContentTypeEnum.GENERATING_CONTENT
      );
      // Mark API call as in progress
      updateChatOverlayState('isApiCallInProgress', true);
      // Reset current error type
      updateIspmErrorState('errorType', undefined);
      // Store user's message in the session
      updateUserMessageInSession(request);
    },
    [
      updateChatOverlayState,
      updateIspmErrorState,
      updateIspmState,
      updateUserMessageInSession,
    ]
  );

  /**
   * Updates the application context after receiving an API response.
   * This function:
   * - Resets certain states like `userQuery`.
   * - Marks the API call as complete and re-enables the input field.
   * - Updates the session with the response.
   * - Handles cases where there is an error in the response.
   *
   * @param {AskApiResponse | SampleDataAPIResponse | undefined} response - The API response payload.
   * @param {AskApiRequest | SampleDataApiRequest} request - The original API request payload.
   */
  const updateContextAfterMakingApiCall = useCallback(
    (
      response: AskApiResponse | SampleDataAPIResponse | undefined,
      request: AskApiRequest | SampleDataApiRequest
    ) => {
      if (!response) {
        return;
      }

      // reset userQuery. Based on its value change - we are making calls to savi
      updateIspmState('userQuery', '');
      // Reset api call  progress to false.
      updateChatOverlayState('isApiCallInProgress', false);
      updateChatOverlayState('inputDisabled', false);

      let tempRequest;

      if ('request' in response) {
        tempRequest = response.request;
      }

      // Combine the response with the request for future reference
      const updatedResponseWithRequest = {
        ...response,
        request: {
          ...tempRequest,
          ...request,
        } as unknown as (AskApiRequest | SampleDataApiRequest) &
          RequestWithFreeText,
      };

      // Store the updated response with the request in the session
      updateApiResponseInSession(updatedResponseWithRequest);

      // If the response indicates a error other than ispm server error, return early.
      // don't update the session chatuuid
      if (!response.chatuuid || response.chatuuid === 'error') {
        return;
      }

      // Update ISPM state with the latest `chatuuid` from the response
      updateIspmState('chatuuid', response.chatuuid);
      // Update the chat overlay content panel with the new response data
      updateChatOverlayState('contentPanelData', updatedResponseWithRequest);
    },
    [updateApiResponseInSession, updateChatOverlayState, updateIspmState]
  );

  return { updateContextBeforeMakingApiCall, updateContextAfterMakingApiCall };
};

export default useContextManagement;
